import { FileText } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Card, CardHeader, CardSubtitle, CardTitle } from '@/components/ui/Card'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tag } from '@/components/ui/Tag'
import { CAP, can } from '@/lib/can'
import { formatNumber } from '@/lib/format'
import { refusalMessage } from '@/lib/refusals'
import { useSession } from '@/features/auth/api/useSession'
import { useCaseInvoices } from '@/features/finance/api/useInvoices'
import {
  useCaseDocuments,
  useRaiseDocument,
} from '@/features/finance/api/useFinance'
import { CaseRateSchedule } from '@/features/cases/components/CaseRateSchedule'
import { receivableOf, useCaseReceivables } from '@/features/finance/api/useReceivables'
import { receivableLabel } from '@/features/finance/allocationLabel'
import { DocumentRowActions } from '@/features/finance/components/DocumentRowActions'
import { ReceivableStatusPill } from '@/features/finance/components/ReceivableStatusPill'
import {
  DebitNoteFormSheet,
  type DebitNotePayer,
} from '@/features/finance/components/FinanceFormSheets'
import { useRaiseInvoiceDraft } from '@/features/finance/api/useRaiseInvoiceDraft'
import { NewDocumentMenu } from '@/features/finance/components/NewDocumentMenu'
import { mayIssueDocument } from '@/features/finance/documentAccess'
import { documentStatusOf } from '@/types/domain'
import type { CaseDetail, DocumentStatus, Receivable } from '@/types/domain'

/* The case's INVOICES section: the rate that was agreed, the payments it is
 * collected in, and the paper raised against it.
 *
 * ── Why this grew here rather than beside the Documents tab ────────────────
 *
 * It IS the Documents tab, renamed. The rail key stays `documents` — it is what
 * the URL carries, what `BackToCase` links to and what a colleague has already
 * pasted into a chat — and only the label moved, to "Invoices". A separate
 * "Invoices" section next to a "Documents" one would be two tabs about one
 * piece of paper, and the reader's first act on every visit would be guessing
 * which of the two holds the invoice they came for. There is one place a case's
 * billing is read, and the documents table is the bottom half of it: the
 * schedule says what is still to be asked for, and the table says what has
 * actually been sent.
 *
 * The rate and the schedule live in `CaseRateSchedule`; what follows is the
 * archive half.
 *
 * The paper raised against one case: its invoices and its debit notes, in the
 * place the person arguing about them is already standing.
 *
 * The section is a VIEW of the finance archive, not a second one. It reads the
 * same two endpoints the Money page reads — `/cases/:id/invoices` and
 * `/cases/:id/debit-notes` — through the same hooks and so out of the same cache
 * entries, renders the same status vocabulary, and raises through the same sheet
 * and the same mutation. So a debit note opened here appears on the Money page
 * without a refresh, and neither surface can grow a rule the other does not
 * have. What the case adds is the filter and the prefill: the case and its
 * client are facts this page already knows, and making somebody retype them
 * onto the document is how an invoice ends up filed against the wrong case.
 *
 * (Until issue #27 the same paragraph described a FIXTURE: both screens called
 * `/api/finance/documents`, which is `internal/showcase` — memory in the server
 * process, where a document raised here was stored nowhere and could not be
 * found again. The argument survives the move unchanged; only the premise
 * became true.)
 *
 * Money vs Documents is the split between the deal and its paper. Money answers
 * "what did this case cost and what is still owed"; this answers "what have we
 * actually sent the client, and has it been paid". They are next to each other
 * on the rail because that is the order the questions get asked in.
 *
 * ── The three stages, and where the actions live now ───────────────────────
 *
 * A document here is DRAFT, then RENDERED, then ISSUED, and the three are three
 * separate presses because they are three separate decisions (00-decisions §1).
 * Generating is not issuing (UX-DR-15): two calls, one confirmation on the
 * irreversible one, and no "issue on generate" shortcut — which is exactly what
 * `document.Handlers.Issue`'s comment refuses, since a flag is something a
 * client sets by accident.
 *
 * Those presses used to live in a 400px panel beside this table, against
 * whichever row was selected, and the argument for the panel was that its
 * buttons named ONE document — "a Generate button repeated down a table is a
 * button somebody presses on whichever line their finger landed on". The
 * concern was right and the remedy was expensive: the panel took a third of the
 * card away from a five-column list of money, and it also held a hand-drawn
 * approximation of the document, which is a document nobody has checked.
 *
 * Both halves moved, and the concern is answered better than the panel answered
 * it. The actions are in `DocumentRowActions`, on the row they act on — an icon
 * inside a row names its document more plainly than a panel floating beside a
 * selection — and the two irreversible ones do not act on the press at all:
 * they open a dialogue that names the consequence in words. The preview is now
 * the SERVER's own page, composed by the same template the client's PDF comes
 * off (`InvoicePreviewSheet`), so the screen can no longer disagree with the
 * paper.
 *
 * ── Who it is for, and what a reader without a key gets ────────────────────
 *
 * Finance. The server guards each half on its own render capability — the
 * invoices on `document.invoice.render`, the debit notes on
 * `document.debitnote.render` — and issuing on a separate key per kind (see
 * `mayIssueDocument`). The section used to ignore all of it, so a Sales,
 * Pricing or Operations reader got a 403 card, a fork whose every item would be
 * refused, a schedule reporting nothing billed with a "Raise invoice" button on
 * each instalment, and Issue icons on every row.
 *
 * So each half is read, offered and raised only for a reader holding its key,
 * and a reader holding neither gets NOTHING from this component: «Документы»
 * hands them `CaseArchiveList` instead (`FilesSection`, by `readsCaseArchive`) — the read-only
 * «Бумаги работы», what paper exists with none of the amounts — and this still
 * renders null if it is ever mounted for them, rather than an empty card
 * asserting that nothing has been raised against the case. A reader holding
 * exactly one key still sees the "Invoices and debit notes" title and its
 * empty state, which overclaim a little, and sees no status updates here (they
 * are archive rows, not finance rows); both are deferred, not missed.
 */

/** The pill's tone per derived status.
 *
 *  Four rows for four states, and there is no `void`: AD-24 makes Draft →
 *  Issued one-way and nothing in this system moves a document back out of
 *  Issued — a correction is a NEW document, which is how the paper world does
 *  it. A fifth row here would be a tone waiting for a state nothing can produce,
 *  the same defect the money section had when it offered "money is not shown for
 *  your role" to a founder holding every role.
 *
 *  Tones only, never translated strings: this is a module constant evaluated
 *  once at import, and a word baked in here would freeze in whichever language
 *  was active at startup. */
const DOC_STATUS_TONES: Record<
  DocumentStatus,
  'neutral' | 'brand' | 'positive' | 'attention' | 'negative'
> = {
  draft: 'neutral',
  issued: 'brand',
  'part-paid': 'attention',
  paid: 'positive',
}

export function CaseDocuments({
  record,
  onPay,
  raiseVariant,
}: {
  record: CaseDetail
  /** Opens «Платёж клиента» on one issued document's row (S-21) — absent for
   *  a reader who may not record a client's payment, or a case that takes
   *  none, and the row then offers no link. */
  onPay?: (row: Receivable) => void
  /** The schedule's «Выставить счёт» weight — secondary while «Внести платёж»
   *  is the section's blue act. */
  raiseVariant?: 'primary' | 'secondary'
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  /* What this reader may do here, decided once. RENDER is the gate on a
     document's whole draft life — the list, the raise, the edits, the PDF — so
     it decides which half is read and offered at all; ISSUE is per row. */
  const { data: user } = useSession()
  const mayInvoice = can(user, CAP.documentInvoiceRender)
  const mayDebitNote = can(user, CAP.documentDebitNoteRender)
  const mayIssueInvoice = mayIssueDocument(user, 'invoice')
  const mayIssueDebitNote = mayIssueDocument(user, 'debit-note')

  // The case ID, not its number: the real reads are nested under the id, which
  // is also the id the row scope is checked against. The fixture keyed on the
  // number because it had nothing better. Only the halves this reader may see
  // are asked for: the other answers 403, and the composite would turn that
  // into an error card over the half they CAN see.
  const { data, isPending, isError, error, refetch } = useCaseDocuments(record.id, {
    invoices: mayInvoice,
    debitNotes: mayDebitNote,
  })
  /* The invoices ON THEIR OWN as well as merged into the table above, because
     the schedule needs the one field the merged view drops: `trancheId`, which
     is what says which instalment a demand replaced. Not a second request —
     `useCaseDocuments` calls this same hook, so both readers come out of one
     cache entry and cannot disagree. Gated on the same key, and by passing ''
     rather than skipping the call: a hook cannot be conditional, and the empty
     id is what the hook's own `enabled` reads as "ask nothing". */
  const invoices = useCaseInvoices(mayInvoice ? record.id : '')
  /* What is paid and what is left on each ISSUED document, as the server
     judges it (S-21): an invoice's GROSS total, a debit note's real paid. The
     section's figures used to be composed here — the invoice net of its VAT,
     every debit note unpaid — and both were wrong. One read for the whole
     tab, shared with the header and the schedule. */
  const receivables = useCaseReceivables(record.id)
  /* Which tranche each invoice replaces, for the issue confirmation's
     carry-over sentence — the field the merged view drops. */
  const trancheOf = useMemo(
    () => new Map((invoices.data ?? []).map((invoice) => [invoice.id, invoice.trancheId])),
    [invoices.data],
  )
  const raise = useRaiseDocument()
  /* One raise for both entry points on this section — the fork's "Client
     invoice" and every "Raise invoice" button on the schedule — so a refusal
     lands once and a press in flight turns both off. */
  const raiseInvoice = useRaiseInvoiceDraft(record.id, record.money?.rate.currency)

  /* Who this case's debit notes bill (AD-26): its client, and only its client
     (S-22a). Composed HERE and passed down, because the sheet has no business
     fetching a directory — and the id must be the CASE's client, the one pair
     the create accepts. */
  const payer = useMemo<DebitNotePayer | undefined>(
    () =>
      record.clientId === undefined || record.clientId === ''
        ? undefined
        : { id: record.clientId, name: record.clientName },
    [record.clientId, record.clientName],
  )

  /* The debit-note branch of the fork, and the only branch that opens a form
     at all: the client invoice posts an empty header and goes to its page (see
     `NewDocumentMenu`).

     Straight to the sheet, and the charges are typed. A picker of the case's
     recorded costs used to come first; it offered every actual — the truck
     rates the client invoice already bills among them — so one tick billed
     the client twice. The owner removed it (2026-10-19, S-22a). */
  const startDebitNote = () => setOpen(true)

  /* The sheet serves the debit-note branch only — the invoice branch opens no
     form at all — so it is mounted only for a reader who may raise one. It
     reads nothing; what
     the gate removes is a door, and the trigger for it is already absent from
     the fork (`NewDocumentMenu`). */
  const sheet = mayDebitNote ? (
    <DebitNoteFormSheet
      open={open}
      onClose={() => setOpen(false)}
      /* The prefill, and the whole reason to raise a document from the case
         rather than from Money: the case is a fact this page holds, and both
         creates are nested under it. Nobody retypes a case number here,
         because retyping one is how an invoice ends up filed against the wrong
         shipment. */
      caseId={record.id}
      payer={payer}
      pending={raise.isPending}
      /* Our sentence, never the server's. `raise.error.message` is the problem
         document's `detail` — written server-side in English for a log — and
         putting it on this sheet handed a Russian operator a second language
         at the one moment they are least able to cope with it (AD-22, AD-27).
         `refusalMessage` also names the fields of an incomplete issuing entity
         (FR-64), which the generic mapping cannot: `issuingEntity` is not a
         control on this form, so "the highlighted fields could not be
         accepted" would highlight nothing. */
      submitError={raise.isError ? refusalMessage(raise.error, t) : undefined}
      failure={raise.isError ? raise.error : undefined}
      /* Closed from onSuccess, so a refusal leaves the sheet open holding the
         draft rather than closing over a save that never happened.

         The new note is no longer "selected" afterwards, because there is no
         selection any more: it appears in the table with its own actions on
         its own row. A debit note has no page to navigate to either — unlike
         an invoice, which is why the fork's other branch goes somewhere and
         this one does not. */
      onSubmit={(payload) =>
        raise.mutate(payload, { onSuccess: () => setOpen(false) })
      }
    />
  ) : null

  /* The archive's three states as a NODE rather than three early returns, and
     that is what the restructure bought: the rate and the schedule above are
     read off the CASE record, which has already arrived, and a document list
     still in flight — or refused — must not take them off the screen with it.
     The old section returned a bare skeleton for the whole tab.

     Both reads or neither on the list itself: the composite is pending while
     EITHER is in flight, because a section that showed the invoices while the
     debit notes were still loading would announce three documents and then
     quietly become five. The `data === undefined` half is the same statement in
     the shape TypeScript can narrow — the composite is a plain object rather
     than a discriminated `useQuery` result, so its `isPending` narrows nothing
     by itself. */
  const archive = isError ? (
    <div className="px-[18px] pb-4">
      <ErrorState error={error} onRetry={() => void refetch()} />
    </div>
  ) : isPending || data === undefined ? (
    <div className="px-[18px] pb-4">
      <Skeleton className="h-40 rounded-[var(--radius-DEFAULT)]" />
    </div>
  ) : data.length === 0 ? (
    /* An empty state says what the surface is FOR. It carries no action of its
       own any more: the fork sits in the card header a few pixels above, and a
       second control with the same accessible name is two answers to one
       question — for a screen reader, and for a test. */
    <EmptyState
      icon={FileText}
      title={t('cases.documents.emptyTitle')}
      description={t('cases.documents.emptyDescription')}
    />
  ) : (
    /* ONE table, the full width of the card.
     *
     * It used to be a table beside a 400px panel that held the selected
     * document's actions and a hand-drawn sketch of its layout. The panel took
     * roughly a third of the card from a five-column list of money — the
     * counterparty wrapped onto two lines and the outstanding column fell off
     * the right edge — and what it bought was an approximation of a document
     * nobody had checked, plus three buttons naming one row.
     *
     * Both jobs moved. The actions live on the row they act on, which names
     * the document more plainly than a panel beside a selection ever did; the
     * preview is now the SERVER's page, composed by the same template the
     * client's PDF comes off, opened in a sheet from the row. There is
     * therefore nothing left to select, and `selectedId` went with the panel.
     *
     * `relative`, and it is load-bearing: the actions column's header is an
     * `sr-only` label, absolutely positioned, and with no positioned ancestor
     * its containing block is the page — it escaped this box and widened the
     * document to 523px at a 390px viewport (S-11, frontend README).
     */
    <div className="asa-scroll relative min-w-0 overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-line border-b">
                <th scope="col" className="asa-eyebrow h-9 px-[18px]">
                  {t('common.fields.number')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3">
                  {t('finance.balances.counterparty')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3">
                  {t('common.fields.status')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                  {t('common.fields.total')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                  {t('finance.receivables.paid')}
                </th>
                <th scope="col" className="asa-eyebrow h-9 px-3 text-right">
                  {t('finance.receivables.outstanding')}
                </th>
                {/* The actions column carries no heading. "Actions" over a row
                    of icons that each name themselves is a word that tells
                    nobody anything, and the empty header keeps the column
                    widths honest. */}
                <th scope="col" className="h-9 w-px px-[18px]">
                  <span className="sr-only">{t('common.actions.label')}</span>
                </th>
              </tr>
            </thead>
            {/* There is NO Due column, and its absence is a correction rather
                than a simplification. The fixture served a `dueOn` and this
                table washed a row red against it; neither `InvoiceSummary` nor
                `DebitNote` carries a due date at all, and the nearest real fact
                — the issuing entity's payment terms — is a phrase PRINTED on the
                paper rather than a date this side may compute one from. A column
                derived from it would put a deadline on screen that no record
                holds and nobody agreed to, which is worse than no column. The
                `overdue` derivation went with it. */}
            <tbody>
              {(data ?? []).map((document) => {
                const status = documentStatusOf(document)
                /* The served row for an ISSUED document; a draft is not a
                   receivable and keeps «Черновик» with no figures to owe. */
                const served = document.issued
                  ? receivableOf(
                      receivables.data,
                      document.kind === 'invoice' ? 'invoice' : 'debit_note',
                      document.id,
                    )
                  : undefined
                const payable =
                  served !== undefined &&
                  served.allocatable &&
                  /[1-9]/.test(served.outstanding.amount) &&
                  !served.outstanding.amount.startsWith('-')
                return (
                  <tr
                    key={document.id}
                    className="border-line-hair hover:bg-surface-hover h-[38px] border-b transition-colors last:border-b-0"
                  >
                    <td className="px-[18px]">
                      {/* The number, and on an invoice it is the way in: a link
                          rather than a button, because it goes to a page and a
                          reader is entitled to open it in a new tab. A debit
                          note has no page of its own, so it renders as plain
                          text rather than as a link that goes nowhere. */}
                      <span className="flex items-center gap-2">
                        {/* Both numbering styles coexist by design: the Ashgabat
                            AB-2026-0184 form and the Yiwu ASADN26070001 form
                            resolve from the issuing entity (FR-68). A draft has
                            NO number — minting belongs to the issuing
                            transaction (AD-12) — so the word "Draft" stands
                            where the number will be, as a label and never as a
                            value. The fixture printed the literal string "DRAFT"
                            as the number, which is the thing this is not. */}
                        {document.kind === 'invoice' ? (
                          <Link
                            to={`/cases/${document.caseId}/invoices/${document.id}`}
                            className="text-brand font-mono text-[12.5px] font-semibold hover:underline"
                          >
                            {document.number ?? t('finance.documents.preview.draftBadge')}
                          </Link>
                        ) : (
                          <span className="text-brand font-mono text-[12.5px] font-semibold">
                            {document.number ?? t('finance.documents.preview.draftBadge')}
                          </span>
                        )}
                        <Tag>{t(`documentKind.${document.kind}`)}</Tag>
                      </span>
                    </td>
                    <td className="text-text-body px-3 text-[13px]">
                      {/* AD-6: a debit note's recipient is withheld from a caller
                          without agent-identity visibility, and the marker is
                          rendered rather than a blank — a blank reads as a field
                          nobody filled in, which is somebody's cue to go "fix"
                          it. The money on the row stays visible either way; only
                          the identity is suppressed, which is the whole of
                          FR-29. */}
                      <SuppressedValue value={document.counterpartyName} />
                    </td>
                    <td className="px-3">
                      {/* The server's word for an issued document (S-21);
                          until the read answers, and for a draft, the one
                          derived helper both Documents surfaces call. */}
                      {served ? (
                        <ReceivableStatusPill row={served} />
                      ) : (
                        <StatusPill tone={DOC_STATUS_TONES[status]}>
                          {t(`docStatus.${status}`)}
                        </StatusPill>
                      )}
                    </td>
                    <td className="asa-num text-text-body px-3 text-right text-[13px] whitespace-nowrap">
                      {formatNumber((served?.total ?? document.total).amount, 2)}
                      <span className="text-text-muted ml-1 text-[11.5px] font-light">
                        {(served?.total ?? document.total).currency}
                      </span>
                    </td>
                    <td className="asa-num text-text-body px-3 text-right text-[13px]">
                      {served ? formatNumber(served.paid.amount, 2) : '—'}
                    </td>
                    <td className="asa-num text-text-heading px-3 text-right text-[13px]">
                      {served ? formatNumber(served.outstanding.amount, 2) : '—'}
                    </td>
                    <td className="px-[18px]">
                      <span className="flex items-center justify-end gap-3">
                        {/* The way to pay THIS document: «Платёж клиента» opens
                            with the row ticked for its whole outstanding. */}
                        {payable && onPay ? (
                          <button
                            type="button"
                            aria-label={t('finance.receivables.payFor', {
                              target: receivableLabel(t, served),
                            })}
                            className="text-text-link hover:text-text-link-hover cursor-pointer text-[12.5px] whitespace-nowrap"
                            onClick={() => onPay(served)}
                          >
                            {t('finance.receivables.pay')}
                          </button>
                        ) : null}
                        {/* Keyed by the record, so moving between documents never
                            leaves one row's refusal standing under another's
                            icons. */}
                        <DocumentRowActions
                          key={document.id}
                          record={document}
                          trancheId={
                            document.kind === 'invoice' ? trancheOf.get(document.id) : undefined
                          }
                          mayIssue={
                            document.kind === 'invoice' ? mayIssueInvoice : mayIssueDebitNote
                          }
                          onOpen={
                            document.kind === 'invoice'
                              ? () =>
                                  void navigate(
                                    `/cases/${document.caseId}/invoices/${document.id}`,
                                  )
                              : undefined
                          }
                        />
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
    </div>
  )

  /* Nothing at all for a reader holding neither key — after every hook above
     has run, since hooks cannot return early. The rail does not offer this
     section to them; this is what a pasted deep link meets. */
  if (!mayInvoice && !mayDebitNote) return null

  return (
    /* Two cards, one section, read top to bottom: what the client agreed to pay
       and what is left of it, then what has actually been sent. */
    <div className="flex min-w-0 flex-col gap-4">
      {/* Invoice territory only. Its billed figures are joined out of the
          invoice read, so for a reader without it the card would report every
          instalment unbilled — and offer a "Raise invoice" button on each. */}
      {mayInvoice ? (
        <CaseRateSchedule
          record={record}
          invoices={invoices.data ?? []}
          raiseInvoice={raiseInvoice}
          raiseVariant={raiseVariant}
        />
      ) : null}

      <Card>
        <CardHeader
          action={
            <NewDocumentMenu
              caseId={record.id}
              currency={record.money?.rate.currency}
              onDebitNote={startDebitNote}
              raiseInvoice={raiseInvoice}
            />
          }
        >
          <CardTitle>{t('cases.documents.title')}</CardTitle>
          <CardSubtitle>{t('cases.documents.subtitle')}</CardSubtitle>
        </CardHeader>

        {archive}
      </Card>

      {sheet}
    </div>
  )
}
